import { apiFetch, jsonOpts, parseJson } from "./client";
import type {
  AttachmentUpload,
  FieldVisit,
  FieldVisitInput,
  FieldVisitUpdate,
  ReportAttachment,
  UserRef,
  VisitReport,
  VisitReportInput,
} from "./types";

/** Newest first, as the backend orders them. */
export async function getAccountVisits(
  accountId: string
): Promise<FieldVisit[]> {
  return parseJson<FieldVisit[]>(
    await apiFetch(`/api/customers/accounts/${accountId}/visits`)
  );
}

export async function createVisit(
  accountId: string,
  body: FieldVisitInput
): Promise<FieldVisit> {
  return parseJson<FieldVisit>(
    await apiFetch(`/api/customers/accounts/${accountId}/visits`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify(body),
    })
  );
}

/** Only while the visit is scheduled; absent keys stay as they are. */
export async function updateVisit(
  visitId: string,
  body: FieldVisitUpdate
): Promise<FieldVisit> {
  return parseJson<FieldVisit>(
    await apiFetch(`/api/visits/${visitId}`, {
      ...jsonOpts,
      method: "PATCH",
      body: JSON.stringify(body),
    })
  );
}

/** scheduled -> not_done. The server refuses a blank reason or any other state. */
export async function markNotDone(
  visitId: string,
  reason: string
): Promise<FieldVisit> {
  return parseJson<FieldVisit>(
    await apiFetch(`/api/visits/${visitId}/not-done`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify({ reason }),
    })
  );
}

/** scheduled or draft -> cancelled; a draft report is discarded. */
export async function cancelVisit(
  visitId: string,
  reason: string
): Promise<FieldVisit> {
  return parseJson<FieldVisit>(
    await apiFetch(`/api/visits/${visitId}/cancel`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify({ reason }),
    })
  );
}

/** not_done -> a new scheduled visit; the server copies everything but the date. */
export async function rescheduleVisit(
  visitId: string,
  scheduledAt: string
): Promise<FieldVisit> {
  return parseJson<FieldVisit>(
    await apiFetch(`/api/visits/${visitId}/reschedule`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    })
  );
}

/** Who may join the visit as a participant: every user but its FAS. */
export async function getParticipantUsers(visitId: string): Promise<UserRef[]> {
  return parseJson(await apiFetch(`/api/visits/${visitId}/participant-users`));
}

export async function getVisit(visitId: string): Promise<FieldVisit> {
  return parseJson(await apiFetch(`/api/visits/${visitId}`));
}

/** `null` until the report is started: a 404 here is an answer, not a failure. */
export async function getReport(visitId: string): Promise<VisitReport | null> {
  const res = await apiFetch(`/api/visits/${visitId}/report`);
  return res.status === 404 ? null : parseJson(res);
}

export async function startReport(visitId: string): Promise<VisitReport> {
  return parseJson(
    await apiFetch(`/api/visits/${visitId}/report`, { method: "POST" })
  );
}

export async function saveReport(
  visitId: string,
  content: VisitReportInput
): Promise<VisitReport> {
  return parseJson(
    await apiFetch(`/api/visits/${visitId}/report`, {
      ...jsonOpts,
      method: "PUT",
      body: JSON.stringify(content),
    })
  );
}

export async function submitReport(visitId: string): Promise<VisitReport> {
  return parseJson<VisitReport>(
    await apiFetch(`/api/visits/${visitId}/report/submit`, { method: "POST" })
  );
}

const attachmentsUrl = (visitId: string) =>
  `/api/visits/${visitId}/report/attachments`;

export async function getReportAttachments(
  visitId: string
): Promise<ReportAttachment[]> {
  return parseJson(await apiFetch(attachmentsUrl(visitId)));
}

/**
 * Three steps, so a 50 MB video never crosses the API: ask for an upload
 * ticket, post the bytes straight to the bucket, then confirm them.
 */
export async function uploadReportAttachment(
  visitId: string,
  kind: ReportAttachment["kind"],
  file: File
): Promise<ReportAttachment> {
  const ticket = await parseJson<AttachmentUpload>(
    await apiFetch(`${attachmentsUrl(visitId)}/uploads`, {
      ...jsonOpts,
      method: "POST",
      body: JSON.stringify({
        content_type: file.type || "application/octet-stream",
        size: file.size,
      }),
    })
  );
  // S3 reads the signed policy fields first and the file last.
  const body = new FormData();
  for (const [name, value] of Object.entries(ticket.fields)) {
    body.append(name, value);
  }
  body.append("file", file);
  const stored = await fetch(ticket.url, { method: "POST", body });
  if (!stored.ok) {
    throw new Error(`${ticket.url} -> ${stored.status}`);
  }
  return parseJson(
    await apiFetch(`${attachmentsUrl(visitId)}/${ticket.attachment_id}`, {
      ...jsonOpts,
      method: "PUT",
      body: JSON.stringify({ kind, filename: file.name }),
    })
  );
}

export async function deleteReportAttachment(
  visitId: string,
  attachmentId: string
): Promise<void> {
  const res = await apiFetch(`${attachmentsUrl(visitId)}/${attachmentId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    await parseJson(res);
  }
}

/**
 * The sent report as a PDF, saved through a blob. Fetched rather than linked so
 * an expired access token refreshes and an error reaches the FAS instead of
 * landing in their downloads as `pdf.json`. The language and time zone are
 * the FAS's, so the PDF reads like the app.
 */
export async function downloadReportPdf(
  visitId: string,
  lang: string
): Promise<void> {
  const res = await apiFetch(
    `/api/visits/${visitId}/report/pdf?${new URLSearchParams({
      lang,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })}`
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const filename =
    res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] ??
    "visit-report.pdf";
  const href = URL.createObjectURL(await res.blob());
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(href);
}

/** Opened by the browser with its session cookie; it redirects to the bucket. */
export const reportAttachmentUrl = (visitId: string, attachmentId: string) =>
  `${attachmentsUrl(visitId)}/${attachmentId}`;
